/** @jsx jsx */
import { jsx } from 'theme-ui';
import React, { useCallback } from 'react';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  useToast,
} from '@chakra-ui/core';
import { registerLocale } from 'react-datepicker';
import { enGB, fr } from 'date-fns/locale';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { drawerCSS } from '../CalendarView.Style';
import { reactDatePickerCSS } from './ReactDatePicker.Style';
import { useStoreActions } from '../../../models/hooks';
import {
  ActiveAppointmentAvailabilitySlot,
  AppointmentAvailability,
  AppointmentType,
} from '../../../firebase/firestore/documents/appointmentAvaiability';
import { loginButtonStyle } from '../../auth/login/loginForm/LoginForm.styles';
import colors from '../../../styles/colors';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { TimezoneValue } from './TimezoneSelect';
import { useBookingForm } from './useBookingForm';
import { DateSelection } from './DateSelection';
import { TimeSelection } from './TimeSelection';
import { AppointmentSection } from './AppointmentSelection';

registerLocale('fr', enGB);
registerLocale('fr', fr);

export type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  redirectToPreviousPageHandler: () => void;
  activeAppointmentSlot: ActiveAppointmentAvailabilitySlot | undefined;
  scheduleDetail?: AppointmentAvailability;
};

export const BookingSchedule = ({
  isOpen,
  onClose,
  redirectToPreviousPageHandler,
  scheduleDetail,
  activeAppointmentSlot,
}: DrawerProps): JSX.Element => {
  const { t } = useTranslation('calendar');
  const toast = useToast();
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const { saveSchedule, updateSchedule } = useStoreActions((actions) => actions.calendar);
  const { currentUserProfile } = useCurrentUserProfile();

  const primaryTimezone = currentUserProfile?.primaryTimezone;
  const isSecondaryTimezoneEnabled = currentUserProfile?.isSecondaryTimezoneEnabled ?? false;
  const isCustomTimezoneCalendar = isSecondaryTimezoneEnabled && !!primaryTimezone?.name;

  const {
    appointmentTypes,
    startDate,
    startTime,
    endTime,
    bookingCapacity,
    selectedTimezone,
    isInPersonOnly,
    setAppointmentTypes,
    setEndTime,
    setBookingCapacity,
    setSelectedTimezone,
    setStartDateOfAvailability,
    setStartTimeOfAvailability,
    clearAllFields,
    getCurrentTimeInSelectedTZ,
    buildBookingDates,
  } = useBookingForm({
    scheduleDetail,
    activeAppointmentSlot,
    isCustomTimezoneCalendar,
    primaryTimezone: primaryTimezone as TimezoneValue,
  });

  const onBookingDrawerClose = useCallback(() => {
    setIsSaving(false);
    clearAllFields();
    onClose();
  }, [onClose, clearAllFields]);

  const onSaveSuccess = useCallback(
    (description: string) => {
      toast({
        title: t('eventForm.successTitle'),
        description,
        duration: 5000,
        isClosable: true,
      });
      onBookingDrawerClose();
      redirectToPreviousPageHandler();
    },
    [toast, t, onBookingDrawerClose, redirectToPreviousPageHandler],
  );

  const saveBooking = useCallback(async () => {
    if (_isNil(startDate) || _isNil(startTime) || _isNil(endTime) || _isEmpty(appointmentTypes)) {
      toast({
        status: 'warning',
        title: t('eventForm.errorTitle'),
        description: t('eventForm.errorMessage'),
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    const dates = buildBookingDates();
    if (!dates) return;

    const { formattedStartDate, formattedEndDate } = dates;

    if (scheduleDetail?.id) {
      await updateSchedule({
        id: scheduleDetail.id,
        payload: { fromDate: formattedStartDate, toDate: formattedEndDate, appointmentTypes, bookingCapacity },
      });
      onSaveSuccess(t('availabilityForm.updateMessage'));
    } else {
      await saveSchedule({
        fromDate: formattedStartDate,
        toDate: formattedEndDate,
        appointmentTypes,
        bookingCapacity,
      });
      onSaveSuccess(t('availabilityForm.saveMessage'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, startTime, endTime, appointmentTypes, bookingCapacity, scheduleDetail, buildBookingDates, onSaveSuccess]);

  const handleAppointmentTypesChange = useCallback(
    (types: AppointmentType[]) => {
      setAppointmentTypes(types);
      if (!isInPersonOnly()) setBookingCapacity(1);
    },
    [isInPersonOnly, setAppointmentTypes, setBookingCapacity],
  );

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={/* istanbul ignore next */ () => onBookingDrawerClose()}
      size="sm"
      data-testid="DrawerTest"
    >
      <DrawerOverlay>
        <DrawerContent css={[drawerCSS, reactDatePickerCSS]}>
          <DrawerCloseButton _focus={{ border: '1px solid #1F3CBA' }} />
          <DrawerHeader borderBottom="2px" borderColor="#EBEDF3" color={colors.blue.default}>
            {t('availabilityForm.myAvailabilityLabel')}
          </DrawerHeader>

          <DrawerBody>
            <DateSelection startDate={startDate} scheduleDetail={scheduleDetail} onChange={setStartDateOfAvailability} />

            <TimeSelection
              startDate={startDate}
              startTime={startTime}
              endTime={endTime}
              scheduleDetail={scheduleDetail}
              isCustomTimezoneCalendar={isCustomTimezoneCalendar}
              selectedTimezone={selectedTimezone}
              getCurrentTimeInSelectedTZ={getCurrentTimeInSelectedTZ}
              onStartTimeChange={setStartTimeOfAvailability}
              onEndTimeChange={setEndTime}
              onTimezoneChange={setSelectedTimezone}
            />

            <AppointmentSection
              appointmentTypes={appointmentTypes}
              bookingCapacity={bookingCapacity}
              isInPersonOnly={isInPersonOnly}
              onAppointmentTypesChange={handleAppointmentTypesChange}
              onBookingCapacityChange={setBookingCapacity}
            />
          </DrawerBody>

          <Flex justify="space-between" py={3} borderTop="2px" borderColor="#EBEDF3">
            <Button
              isLoading={isSaving}
              disabled={isSaving}
              data-testid="SaveScheduleBtn"
              css={loginButtonStyle}
              borderRadius={3}
              ml={6}
              onClick={saveBooking}
            >
              {t('availabilityForm.saveAvailability')}
            </Button>
            <Button variant="outline" borderRadius={3} mr={3} onClick={onBookingDrawerClose} data-testid="CancelDrawerBtn">
              {t('eventForm.cancel')}
            </Button>
          </Flex>
        </DrawerContent>
      </DrawerOverlay>
    </Drawer>
  );
};
